import { useEvent } from "@oysterhr/common"
import { SelectionState } from "@oysterhr/ui"
import { constant, noop } from "lodash-es"
import { createContext, useCallback, useContext, useMemo } from "react"
import { useUrlQueryParamContext } from "@oysterhr/quarantine"

export const SELECTED_ENTRIES_PARAM_NAME = "selectedEntries"

type InternalSelectedIds = "ALL_RECORDS" | "ALL_IN_VIEW" | string[]

type SelectedIds = Exclude<InternalSelectedIds, "ALL_IN_VIEW">

const areEntriesSelectedFromHeader = (
  entries: InternalSelectedIds | string | null,
): entries is "ALL_IN_VIEW" | "ALL_RECORDS" => {
  return entries === "ALL_IN_VIEW" || entries === "ALL_RECORDS"
}

export type UseTableSelectQueryParamStateArgs = {
  queryParamName?: string
  resetOnQueryParamsChange?: boolean
  displayedIds: string[]
}

export type UseTableSelectQueryParamStateController = {
  selectedIds: SelectedIds
  isSelected: (entity: string | { id: string }) => boolean
  onChangeMultiSelectionState: (selectionState: SelectionState) => void
  onToggleRow: (entry: string | { id: string }) => void
  headerSelectionState: SelectionState
  resetSelection: () => void
}

export function useTableSelectQueryParamState({
  queryParamName = SELECTED_ENTRIES_PARAM_NAME,
  displayedIds,
}: UseTableSelectQueryParamStateArgs): UseTableSelectQueryParamStateController {
  const {
    setParam,
    params: { [queryParamName]: selectedEntriesParam },
  } = useUrlQueryParamContext()

  const selectedIds: SelectedIds = useMemo(() => {
    if (!selectedEntriesParam) return []

    if (areEntriesSelectedFromHeader(selectedEntriesParam as InternalSelectedIds)) {
      return selectedEntriesParam as SelectedIds
    }

    // one id entry will get parsed as a single string
    return (selectedEntriesParam as string).split(",")
  }, [selectedEntriesParam])

  const selectedEntriesSet: Set<string> = useMemo(() => {
    if (typeof selectedIds === "string") return new Set()
    return new Set(selectedIds)
  }, [selectedIds])

  const _isSelectedId = useCallback(
    (id: string): boolean => {
      return areEntriesSelectedFromHeader(selectedIds) || selectedEntriesSet.has(id)
    },
    [selectedIds, selectedEntriesSet],
  )

  const isSelected = useCallback(
    (entity: string | { id: string }): boolean => {
      if (typeof entity === "object") {
        return _isSelectedId(entity.id)
      }

      return isSelected(entity)
    },
    [_isSelectedId],
  )

  const setSelectedEntries = useCallback(
    (value: InternalSelectedIds | null) => {
      if (!value) {
        setParam(queryParamName, "")
        return
      }
      if (Array.isArray(value)) {
        setParam(queryParamName, value.join(","))
        return
      }

      if (value === "ALL_IN_VIEW") {
        setParam(queryParamName, displayedIds.join(","))
        return
      }

      setParam(queryParamName, value)
    },
    [queryParamName, setParam, displayedIds],
  )

  const onChangeMultiSelectionState = useCallback(
    (selectionState: SelectionState) => {
      setSelectedEntries(
        selectionState === "NONE" || selectionState === "SOME" ? null : selectionState,
      )
    },
    [setSelectedEntries],
  )

  const _toggleSelection = useEvent((id: string) => {
    if (selectedIds === "ALL_RECORDS") {
      setSelectedEntries(displayedIds.filter(entryId => entryId !== id))
      return
    }

    if (selectedEntriesSet.has(id)) {
      // a mutation to the object, but that's okay because it will trigger a change in query params and a re-evaluation
      selectedEntriesSet.delete(id)
      setSelectedEntries([...selectedEntriesSet])
    } else {
      setSelectedEntries([...selectedEntriesSet, id])
    }
  })

  const onToggleRow = useCallback(
    (entry: string | { id: string }) => {
      if (typeof entry === "object") return _toggleSelection(entry.id)

      return _toggleSelection(entry)
    },
    [_toggleSelection],
  )

  const resetSelection = useCallback(() => setSelectedEntries(null), [setSelectedEntries])

  const headerSelectionState: SelectionState = useMemo(() => {
    if (!Array.isArray(selectedIds)) return selectedIds

    if (selectedIds.length === displayedIds.length && displayedIds.length) return "ALL_IN_VIEW"

    return selectedIds.length ? "SOME" : "NONE"
  }, [selectedIds, displayedIds])

  return {
    selectedIds,
    isSelected,
    onChangeMultiSelectionState,
    onToggleRow,
    headerSelectionState,
    resetSelection,
  }
}

export const SelectedTableEntriesContext = createContext<
  UseTableSelectQueryParamStateController & { initialState?: boolean }
>({
  headerSelectionState: "NONE",
  isSelected: constant(false),
  onChangeMultiSelectionState: noop,
  onToggleRow: noop,
  resetSelection: noop,
  selectedIds: [],
  initialState: true,
})

export function useSelectedTableEntriesContext() {
  const contextData = useContext(SelectedTableEntriesContext)

  return contextData
}
